import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Autocomplete, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Alert, CircularProgress, Chip, MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import BusinessIcon from '@mui/icons-material/Business';
import axios from 'axios';

const API = '/api/rrhh';
const fmt = v => Number(v || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = d => d ? new Date(d).toLocaleDateString('es-PE', { timeZone: 'UTC' }) : '—';

const ASEGURADORAS = [
  'Rimac Seguros', 'Pacifico Seguros', 'La Positiva Seguros', 'Mapfre Perú', 'HDI Seguros',
  'Interseguro', 'BNP Paribas Cardif', 'Chubb Perú', 'Otra',
];

export default function SeguroVidaRRHH() {
  const [empresas, setEmpresas] = useState([]);
  const [empresa, setEmpresa] = useState(null);
  const [seguros, setSeguros] = useState([]);
  const [sinSeguro, setSinSeguro] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vista, setVista] = useState('activos'); // 'activos' | 'alerta'

  const [dlgOpen, setDlgOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [trabajadores, setTrabajadores] = useState([]);
  const [form, setForm] = useState({
    trabajador: null, aseguradora: '', numero_poliza: '', prima_mensual: '',
    fecha_inicio: '', fecha_fin: '', estado: 'VIGENTE', observaciones: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/empresas`).then(r => setEmpresas(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresa) return;
    axios.get(`${API}/trabajadores?empresa_id=${empresa.empresa_id}&activos=true`)
      .then(r => setTrabajadores(r.data)).catch(() => {});
  }, [empresa]);

  const cargar = useCallback(() => {
    if (!empresa) return;
    setLoading(true); setError('');
    Promise.all([
      axios.get(`${API}/seguro-vida?empresa_id=${empresa.empresa_id}`),
      axios.get(`${API}/seguro-vida/sin-seguro?empresa_id=${empresa.empresa_id}`),
    ])
      .then(([r1, r2]) => { setSeguros(r1.data); setSinSeguro(r2.data); })
      .catch(e => setError(e.response?.data?.error || 'Error al cargar'))
      .finally(() => setLoading(false));
  }, [empresa]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirCrear = () => {
    setEditando(null);
    setForm({ trabajador: null, aseguradora: '', numero_poliza: '', prima_mensual: '', fecha_inicio: '', fecha_fin: '', estado: 'VIGENTE', observaciones: '' });
    setDlgOpen(true);
  };

  const abrirEditar = (s) => {
    setEditando(s.id);
    const trab = trabajadores.find(t => t.id === s.trabajador_id) || null;
    setForm({
      trabajador: trab, aseguradora: s.aseguradora || '', numero_poliza: s.numero_poliza || '',
      prima_mensual: s.prima_mensual || '', fecha_inicio: s.fecha_inicio?.substring(0, 10) || '',
      fecha_fin: s.fecha_fin?.substring(0, 10) || '', estado: s.estado || 'VIGENTE',
      observaciones: s.observaciones || '',
    });
    setDlgOpen(true);
  };

  const handleGuardar = async () => {
    if (!form.trabajador || !form.aseguradora || !form.fecha_inicio) return;
    setSaving(true);
    try {
      const payload = {
        empresa_id: empresa.empresa_id,
        trabajador_id: form.trabajador.id,
        aseguradora: form.aseguradora,
        numero_poliza: form.numero_poliza,
        prima_mensual: form.prima_mensual,
        fecha_inicio: form.fecha_inicio,
        fecha_fin: form.fecha_fin || null,
        estado: form.estado,
        observaciones: form.observaciones,
      };
      if (editando) {
        await axios.put(`${API}/seguro-vida/${editando}`, payload);
      } else {
        await axios.post(`${API}/seguro-vida`, payload);
      }
      setDlgOpen(false);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const estadoColor = { VIGENTE: 'success', VENCIDO: 'warning', CANCELADO: 'error' };

  return (
    <Box p={2}>
      <Typography variant="h5" fontWeight="bold" color="purple" gutterBottom>
        <HealthAndSafetyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Seguro de Vida Ley
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <Autocomplete
            options={empresas} value={empresa} onChange={(_, v) => { setEmpresa(v); setSeguros([]); setSinSeguro([]); }}
            getOptionLabel={o => o.razon_social || ''}
            sx={{ minWidth: '16em' }}
            renderInput={p => <TextField {...p} label="Empresa" size="small"
              InputProps={{ ...p.InputProps, startAdornment: <BusinessIcon fontSize="small" sx={{ mr: 0.5, color: 'purple' }} /> }} />}
          />
          <Box>
            <Button variant={vista === 'activos' ? 'contained' : 'outlined'} size="small"
              sx={{ mr: 1, ...(vista === 'activos' ? { bgcolor: 'purple' } : { color: 'purple', borderColor: 'purple' }) }}
              onClick={() => setVista('activos')}>
              Pólizas registradas
            </Button>
            <Button variant={vista === 'alerta' ? 'contained' : 'outlined'} size="small"
              sx={vista === 'alerta' ? { bgcolor: '#d32f2f' } : { color: '#d32f2f', borderColor: '#d32f2f' }}
              startIcon={<WarningAmberIcon />} onClick={() => setVista('alerta')}
              disabled={!empresa}>
              Sin seguro ({sinSeguro.length})
            </Button>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} sx={{ bgcolor: 'purple' }}
            onClick={abrirCrear} disabled={!empresa}>
            Nueva Póliza
          </Button>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {empresa && sinSeguro.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}
          icon={<WarningAmberIcon />}
          action={<Button size="small" color="warning" onClick={() => setVista('alerta')}>Ver lista</Button>}>
          <strong>{sinSeguro.length} trabajador(es)</strong> con más de 3 meses de servicio no tienen seguro de vida vigente.
        </Alert>
      )}

      {loading ? (
        <Box p={3} textAlign="center"><CircularProgress color="secondary" /></Box>
      ) : vista === 'alerta' ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#d32f2f' }}>
                {['Trabajador', 'DNI', 'Cargo', 'Área', 'Fecha Ingreso', 'Meses en empresa'].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', fontSize: 12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sinSeguro.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'green' }}>
                  ✓ Todos los trabajadores elegibles tienen seguro de vida
                </TableCell></TableRow>
              ) : sinSeguro.map(t => (
                <TableRow key={t.id} hover sx={{ bgcolor: '#fff8e1' }}>
                  <TableCell sx={{ fontSize: 12 }}>{t.apellido_paterno} {t.apellido_materno}, {t.nombres}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{t.num_doc}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{t.cargo}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{t.area}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{fmtDate(t.fecha_ingreso)}</TableCell>
                  <TableCell sx={{ fontSize: 12, fontWeight: 'bold', color: '#d32f2f' }}>{t.meses_servicio} meses</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'purple' }}>
                {['Trabajador', 'Aseguradora', 'N° Póliza', 'Prima Mensual', 'Vigencia', 'Estado', 'Acciones'].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', fontSize: 12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {seguros.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center">No hay pólizas registradas</TableCell></TableRow>
              ) : seguros.map(s => (
                <TableRow key={s.id} hover>
                  <TableCell sx={{ fontSize: 12 }}>{s.apellido_paterno} {s.apellido_materno}, {s.nombres}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{s.aseguradora}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{s.numero_poliza || '—'}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>S/ {fmt(s.prima_mensual)}</TableCell>
                  <TableCell sx={{ fontSize: 11 }}>
                    {fmtDate(s.fecha_inicio)} — {s.fecha_fin ? fmtDate(s.fecha_fin) : 'Indefinido'}
                  </TableCell>
                  <TableCell>
                    <Chip label={s.estado} color={estadoColor[s.estado] || 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="secondary" onClick={() => abrirEditar(s)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog Nueva/Editar Póliza */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'purple', color: 'white' }}>
          {editando ? 'Editar Póliza' : 'Nueva Póliza de Seguro de Vida'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Autocomplete
            options={trabajadores} value={form.trabajador} disabled={!!editando}
            onChange={(_, v) => setForm(f => ({ ...f, trabajador: v }))}
            getOptionLabel={o => `${o.apellido_paterno} ${o.apellido_materno}, ${o.nombres}`}
            sx={{ minWidth: '16em' }}
            renderInput={p => <TextField {...p} label="Trabajador *" size="small" />}
          />
          <Autocomplete
            options={ASEGURADORAS} value={form.aseguradora || null} freeSolo
            onInputChange={(_, v) => setForm(f => ({ ...f, aseguradora: v }))}
            sx={{ minWidth: '16em' }}
            renderInput={p => <TextField {...p} label="Aseguradora *" size="small" />}
          />
          <TextField label="N° de Póliza" size="small" value={form.numero_poliza}
            onChange={e => setForm(f => ({ ...f, numero_poliza: e.target.value }))} sx={{ minWidth: '16em' }} />
          <TextField label="Prima Mensual (S/)" size="small" type="number" value={form.prima_mensual}
            onChange={e => setForm(f => ({ ...f, prima_mensual: e.target.value }))}
            inputProps={{ min: 0, step: '0.01' }} sx={{ minWidth: '16em' }} />
          <TextField label="Fecha Inicio *" type="date" size="small" value={form.fecha_inicio}
            onChange={e => setForm(f => ({ ...f, fecha_inicio: e.target.value }))}
            InputLabelProps={{ shrink: true }} sx={{ minWidth: '16em' }} />
          <TextField label="Fecha Fin" type="date" size="small" value={form.fecha_fin}
            onChange={e => setForm(f => ({ ...f, fecha_fin: e.target.value }))}
            InputLabelProps={{ shrink: true }} helperText="Dejar vacío si la póliza no tiene vencimiento"
            sx={{ minWidth: '16em' }} />
          <TextField select label="Estado" size="small" value={form.estado}
            onChange={e => setForm(f => ({ ...f, estado: e.target.value }))} sx={{ minWidth: '16em' }}>
            {['VIGENTE', 'VENCIDO', 'CANCELADO'].map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
          </TextField>
          <TextField label="Observaciones" size="small" value={form.observaciones}
            onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))}
            multiline rows={2} sx={{ minWidth: '16em' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Cancelar</Button>
          <Button variant="contained" sx={{ bgcolor: 'purple' }} onClick={handleGuardar} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
